// src/pages/DebtStudio/components/Issues/DealStructure/hooks/solverShared.js

import dayjs from "lib/dayjs";
import { dayCountFraction } from "lib/dayCount";
import { trunc3 } from "pages/DebtStudio/utils/formatters";
import { computeCapI } from "pages/DebtStudio/lib/computeCapI";

/* ────────────────────────────────────────────────
   Canonical rounding helpers
──────────────────────────────────────────────── */
export const roundPrice3 = (p) =>
  Math.round(Number(p || 0) * 1000) / 1000;

export const roundCents = (x) =>
  Math.round(Number(x || 0) * 100) / 100;

export const ceilCents = (x) =>
  Math.ceil((Number(x) || 0) * 100 - 1e-9) / 100;

/* ────────────────────────────────────────────────
   Generic helpers
──────────────────────────────────────────────── */
export function num(x, dflt = 0) {
  const n = Number(x);
  return Number.isFinite(n) ? n : dflt;
}

export function dj(d) {
  return d ? dayjs(d) : null;
}

export function pctOrDec(v) {
  if (v == null || v === "") return null;

  const n = Number(v);
  if (!Number.isFinite(n)) return null;

  return n > 1 ? n / 100 : n;
}

export function normalizeDenom(val) {
  const n = Number(val);

  if (!Number.isFinite(n) || n < 0.01) {
    return 0.01;
  }

  return n;
}

/* ────────────────────────────────────────────────
   Financing type helpers
──────────────────────────────────────────────── */
export function normalizeFinancingType(value) {
  return String(value || "bond").trim().toLowerCase();
}

export function isLoanFinancing(financingType) {
  return normalizeFinancingType(financingType) === "loan";
}

export function shouldUseCommercialLoanPaymentRounding({
  financingType,
}) {
  return isLoanFinancing(financingType);
}

export function shouldBypassDenominationOptimizer({
  financingType,
}) {
  return isLoanFinancing(financingType);
}

/* ────────────────────────────────────────────────
   Frequency helpers
──────────────────────────────────────────────── */
export function principalFreqToStepMonthsFromValue(
  principalFrequency
) {
  const freqToMonths = {
    monthly: 1,
    bimonthly: 2,
    quarterly: 3,
    every_four_months: 4,
    triannual: 4,
    semiannual: 6,
    annual: 12,
  };

  return (
    freqToMonths[
      String(principalFrequency || "annual").toLowerCase()
    ] ?? 12
  );
}

export function couponFreqToStepMonths(couponFrequency) {
  const freq = String(
    couponFrequency || "semiannual"
  ).toLowerCase();

  const map = {
    monthly: 1,
    bimonthly: 2,
    quarterly: 3,
    every_four_months: 4,
    triannual: 4,
    semiannual: 6,
    annual: 12,
  };

  return map[freq] ?? 6;
}

/* ────────────────────────────────────────────────
   Principal date helpers
──────────────────────────────────────────────── */
export function generatePrincipalDateSetFromParams({
  firstPrincipalDate,
  finalMaturityDate,
  principalFrequency = "annual",
}) {
  const fp = dj(firstPrincipalDate);
  const fm = dj(finalMaturityDate);

  if (!fp || !fm || !fp.isValid() || !fm.isValid()) {
    return new Set();
  }

  const stepMonths =
    principalFreqToStepMonthsFromValue(
      principalFrequency
    );

  const s = new Set();

  let cur = fp.startOf("day");

  for (let guard = 0; guard < 5000; guard++) {
    if (cur.isAfter(fm)) break;

    s.add(cur.format("YYYY-MM-DD"));

    cur = cur.add(stepMonths, "month");
  }

  s.add(fm.format("YYYY-MM-DD"));

  return s;
}

export function deriveLdsPrincipalEndDates({
  rows = [],
  firstPrincipalDate,
  finalMaturityDate,
  principalFrequency = "annual",
}) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return [];
  }

  const expanded = [...rows].sort((a, b) =>
    String(a.date).localeCompare(String(b.date))
  );

  const principalDateSet =
    generatePrincipalDateSetFromParams({
      firstPrincipalDate,
      finalMaturityDate,
      principalFrequency,
    });

  const out = [];

  for (const r of expanded) {
    const d = dayjs(r?.date);

    if (!d.isValid()) continue;

    const iso = d.format("YYYY-MM-DD");

    if (principalDateSet.has(iso)) {
      out.push(iso);
    }
  }

  if (!out.length) {
    const fallback = dayjs(
      expanded[expanded.length - 1]?.date
    );

    if (fallback.isValid()) {
      out.push(fallback.format("YYYY-MM-DD"));
    }
  }

  return out;
}

/* ────────────────────────────────────────────────
   Expanded row helpers
──────────────────────────────────────────────── */
export function buildExpandedRows(rows = []) {
  const termGroups = {};

  for (const r of rows) {
    if (r?.bond_type === "term" && r?.term_group) {
      if (!termGroups[r.term_group]) {
        termGroups[r.term_group] = [];
      }

      termGroups[r.term_group].push(r);
    }
  }

  const sharedTermData = new Map();

  Object.values(termGroups).forEach((members) => {
    if (!members.length) return;

    members.sort((a, b) =>
      String(a.date).localeCompare(String(b.date))
    );

    const last = members[members.length - 1];

    const cDec = pctOrDec(last.coupon);

    const anchorRaw = Number.isFinite(last?.price)
      ? Number(last.price)
      : 100;

    const anchorTrunc = trunc3(anchorRaw);

    const anchorPrice =
      anchorTrunc == null ? 100 : anchorTrunc;

    sharedTermData.set(last.term_group, {
      couponDec: cDec,
      price: anchorPrice,
    });
  });

  const expanded = rows.map((r) => {
    if (
      r?.bond_type === "term" &&
      r?.term_group &&
      sharedTermData.has(r.term_group)
    ) {
      const { couponDec, price } =
        sharedTermData.get(r.term_group);

      return {
        ...r,
        coupon:
          couponDec != null && couponDec <= 1
            ? couponDec * 100
            : r.coupon,
        price: trunc3(price ?? 100) ?? 100,
      };
    }

    const p = Number.isFinite(r?.price)
      ? r.price
      : 100;

    return {
      ...r,
      price: roundPrice3(p),
    };
  });

  expanded.sort((a, b) =>
    String(a.date).localeCompare(String(b.date))
  );

  return expanded;
}

/* ────────────────────────────────────────────────
   Segment builders
──────────────────────────────────────────────── */
export function buildSegments({
  expanded = [],
  firstPrincipalDate,
  finalMaturityDate,
  principalFrequency = "annual",
}) {
  const n = expanded.length;

  const principalDateSet =
    generatePrincipalDateSetFromParams({
      firstPrincipalDate,
      finalMaturityDate,
      principalFrequency,
    });

  const principalIdx = [];

  for (let i = 0; i < n; i++) {
    const d = dayjs(expanded[i].date).format(
      "YYYY-MM-DD"
    );

    if (principalDateSet.has(d)) {
      principalIdx.push(i);
    }
  }

  if (principalIdx.length === 0) {
    principalIdx.push(n - 1);
  }

  let start0 = 0;

  try {
    const fp = dj(firstPrincipalDate);

    if (fp && principalIdx.length > 0) {
      const stepMonths =
        principalFreqToStepMonthsFromValue(
          principalFrequency
        );

      const priorBoundary = fp
        .startOf("day")
        .subtract(stepMonths, "month");

      let lastAtOrBefore = -1;

      for (let i = 0; i <= principalIdx[0]; i++) {
        const di = dayjs(expanded[i].date).startOf(
          "day"
        );

        if (
          di.isSame(priorBoundary) ||
          di.isBefore(priorBoundary)
        ) {
          lastAtOrBefore = i;
        }
      }

      start0 = Math.min(
        principalIdx[0],
        Math.max(0, lastAtOrBefore + 1)
      );
    }
  } catch (e) {
    start0 = 0;
  }

  const segments = [];

  for (let y = 0; y < principalIdx.length; y++) {
    const end = principalIdx[y];

    const start =
      y === 0
        ? start0
        : principalIdx[y - 1] + 1;

    segments.push({ start, end });
  }

  return {
    principalIdx,
    start0,
    segments,
  };
}

/* ────────────────────────────────────────────────
   Stub/accrual helpers
──────────────────────────────────────────────── */
export function computeStubFactor({
  startDate,
  endDate,
  dayCount,
  principalStepMonths,
}) {
  const dc = dayCount || "30/360";

  const start = startDate
    ? dayjs(startDate).startOf("day")
    : null;

  const end = endDate
    ? dayjs(endDate).startOf("day")
    : null;

  if (
    !start ||
    !end ||
    !start.isValid() ||
    !end.isValid()
  ) {
    return 1;
  }

  if (!end.isAfter(start, "day")) {
    return 1;
  }

  const stepMonths =
    Number(principalStepMonths) || 12;

  const expectedStart = end
    .clone()
    .subtract(stepMonths, "month");

  const actualFrac = dayCountFraction(
    start.toDate(),
    end.toDate(),
    dc
  );

  const expectedFrac = dayCountFraction(
    expectedStart.toDate(),
    end.toDate(),
    dc
  );

  if (
    !Number.isFinite(actualFrac) ||
    !Number.isFinite(expectedFrac)
  ) {
    return 1;
  }

  if (expectedFrac <= 0) {
    return 1;
  }

  const factor = actualFrac / expectedFrac;

  return Number.isFinite(factor) &&
    factor > 0 &&
    factor < 1
    ? factor
    : 1;
}

export function computeSegmentAccrualFactors({
  datedDate,
  firstCouponDate,
  finalMaturityDate,
  couponFrequency,
  dayCount,
  principalEndDates,
  principalStepMonths,
}) {
  const dc = dayCount || "30/360";

  const dd = datedDate
    ? dayjs(datedDate).startOf("day")
    : null;

  const fc = firstCouponDate
    ? dayjs(firstCouponDate).startOf("day")
    : null;

  const fm = finalMaturityDate
    ? dayjs(finalMaturityDate).startOf("day")
    : null;

  if (
    !dd ||
    !fc ||
    !fm ||
    !Array.isArray(principalEndDates) ||
    !principalEndDates.length
  ) {
    return Array(
      principalEndDates?.length || 0
    ).fill(0);
  }

  const pEnds = principalEndDates
    .map((d) => dayjs(d).startOf("day"))
    .filter((d) => d && d.isValid())
    .sort((a, b) => a.valueOf() - b.valueOf());

  if (!pEnds.length) return [];

  const couponStepMonths =
    couponFreqToStepMonths(couponFrequency);

  const couponDates = [];

  let cur = fc.clone();

  for (let guard = 0; guard < 5000; guard++) {
    if (cur.isAfter(fm)) break;

    couponDates.push(cur.clone());

    cur = cur.add(couponStepMonths, "month");
  }

  const fmIso = fm.format("YYYY-MM-DD");

  if (
    !couponDates.some(
      (d) => d.format("YYYY-MM-DD") === fmIso
    )
  ) {
    if (!fm.isBefore(fc) && !fm.isBefore(dd)) {
      couponDates.push(fm.clone());
    }

    couponDates.sort(
      (a, b) => a.valueOf() - b.valueOf()
    );
  }

  const startEx = [];

  for (let y = 0; y < pEnds.length; y++) {
    if (y === 0) {
      startEx.push(
        pEnds[0]
          .clone()
          .subtract(principalStepMonths, "month")
      );
    } else {
      startEx.push(pEnds[y - 1].clone());
    }
  }

  const accrualFactors = Array(pEnds.length).fill(0);

  let prevCoupon = dd.clone();

  let yPtr = 0;

  for (let i = 0; i < couponDates.length; i++) {
    const payDate = couponDates[i];

    if (!payDate.isAfter(dd)) {
      prevCoupon = payDate.clone();
      continue;
    }

    while (
      yPtr < pEnds.length &&
      payDate.isAfter(pEnds[yPtr])
    ) {
      yPtr++;
    }

    if (yPtr >= pEnds.length) break;

    const start = startEx[yPtr];
    const end = pEnds[yPtr];

    if (
      !(
        payDate.isAfter(start) &&
        (payDate.isSame(end) ||
          payDate.isBefore(end))
      )
    ) {
      prevCoupon = payDate.clone();
      continue;
    }

    const frac = dayCountFraction(
      prevCoupon.toDate(),
      payDate.toDate(),
      dc
    );

    accrualFactors[yPtr] += Number(frac) || 0;

    prevCoupon = payDate.clone();
  }

  return accrualFactors;
}

/* ────────────────────────────────────────────────
   Debt service aggregation
──────────────────────────────────────────────── */
export function buildBaseDebtServiceBySegment({
  baseDebtServiceByPeriod = [],
  wrapBaseByPeriod = [],
  segments = [],
  expanded = [],
}) {
  const source =
    Array.isArray(baseDebtServiceByPeriod) &&
    baseDebtServiceByPeriod.length
      ? baseDebtServiceByPeriod
      : Array.isArray(wrapBaseByPeriod)
        ? wrapBaseByPeriod
        : [];

  const baseMap = new Map(
    source
      .filter((x) => x && typeof x === "object")
      .map((x) => [
        String(x.date || ""),
        num(
          x.total ?? x.amount ?? x.value,
          0
        ),
      ])
      .filter(([d]) => !!d)
  );

  return segments.map((seg) => {
    const d = expanded?.[seg.end]?.date;

    return d && baseMap.has(String(d))
      ? num(baseMap.get(String(d)), 0)
      : 0;
  });
}

/* ────────────────────────────────────────────────
   Cap I helpers
──────────────────────────────────────────────── */
export function computeCapIUse({
  capiEnabled,
  dealType,
  capiDate,
  capiInvestmentRate,
  scheduleForMath,
  prebuiltDebtServiceSchedule = null,
  form = {},
}) {
  if (!capiEnabled) return 0;

  try {
    const capi = computeCapI({
      dealType,
      capiEnabled,
      capiDate,
      capiInvestmentRate,
      rows: scheduleForMath,
      form,
      prebuiltDebtServiceSchedule,
    });

    const capiUse = Number(
      capi?.capiUse ?? capi?.use ?? 0
    );

    return Number.isFinite(capiUse)
      ? roundCents(capiUse)
      : 0;
  } catch (e) {
    console.warn(
      "[solverShared] computeCapI failed",
      e
    );

    return 0;
  }
}

/* ────────────────────────────────────────────────
   Presentation helpers
──────────────────────────────────────────────── */
export function collapseTermGroupsForPresentation(
  scheduleForMath
) {
  const lastByGroup = {};

  for (const r of scheduleForMath) {
    if (
      r?.bond_type === "term" &&
      r?.term_group
    ) {
      const g = r.term_group;

      if (
        !lastByGroup[g] ||
        (r.date && r.date > lastByGroup[g].date)
      ) {
        lastByGroup[g] = r;
      }
    }
  }

  return scheduleForMath.map((r) => {
    if (
      r?.bond_type === "term" &&
      r?.term_group
    ) {
      const isLast =
        lastByGroup[r.term_group] === r;

      return isLast
        ? { ...r }
        : {
            ...r,
            yield: null,
            coupon: null,
          };
    }

    return { ...r };
  });
}